"use client";

import { c as _c } from "react/compiler-runtime";
import GridColumn from "@/components/common/grid-column/grid-column";
import GridRow from "@/components/common/grid-row/grid-row";
import cssUtilities from "@/components/utilities.module.scss";
import FAQ from "@/components/common/faq/faq";
import { ArticleData, GlossaryTermData } from "@/sanity/interfaces";
import { useEffect, useState } from "react";
import ResourceContent from "../resources/resource/resource-content/resource-content";
import ResourceKeyList from "../resources/resource/resource-key-list/resource-key-list";
import ResourceFooter from "../resources/resource/resource-footer/resource-footer";
import BaseLink from "@/components/common/base-link/base-link";
import ResourceReferences from "../resources/resource/resource-references";
import ProductArticles from "../products/product/product-articles/product-articles";
import { usePerformance } from "@/contexts/performance-context";
import { sanitizeHeadingId } from "@/utilities/sanitizeHeadingId";
import AuthorInfo from "../resources/resource/resource-info/author-info";
import ResourceInfo from "../resources/resource/resource-info/resource-info";
import GlossaryTermClientContent from "./glossary-term-client-content";
import ResourceTableOfContents from "../resources/resource/resource-table-of-contents";
import { NavOption, ResourceNavProvider } from "../resources/resource/resource-nav";
interface IGlossaryTermProps {
  data: GlossaryTermData;
  latestArticles?: ArticleData[];
  beforeParamTime: number;
  beforeQueryTime: number;
  afterQueryTime: number;
}
const GlossaryTerm = props => {
  const $ = _c(66);
  const {
    data,
    latestArticles,
    beforeParamTime,
    beforeQueryTime,
    afterQueryTime
  } = props;
  const {
    title,
    definition,
    longDefinition: body,
    modules,
    keyTakeaways
  } = data;
  let t0;
  if ($[0] !== modules) {
    t0 = modules?.find(_temp);
    $[0] = modules;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const faqList = t0;
  const {
    checkTimeElapsed
  } = usePerformance();
  let t1;
  if ($[2] !== modules) {
    t1 = modules?.find(_temp2);
    $[2] = modules;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const referenceModule = t1;
  const [headerHeight, setHeaderHeight] = useState(0);
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const headerElement = document.querySelector("header");
      if (headerElement) {
        setHeaderHeight(headerElement.offsetHeight);
      }
      const handleResize = () => {
        const headerElement_0 = document.querySelector("header");
        if (headerElement_0) {
          setHeaderHeight(headerElement_0.offsetHeight);
        }
      };
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    };
    t3 = [];
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== headerHeight) {
    t4 = () => {
      document.documentElement.style.setProperty("--site-header-height", `${headerHeight}px`);
    };
    t5 = [headerHeight];
    $[6] = headerHeight;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== afterQueryTime || $[10] !== beforeParamTime || $[11] !== beforeQueryTime || $[12] !== checkTimeElapsed) {
    t6 = () => {
      checkTimeElapsed(beforeParamTime, "Glossary Term Page before params started");
      checkTimeElapsed(beforeQueryTime, "Glossary Term Page before query started");
      checkTimeElapsed(afterQueryTime, "Glossary Term Page after query completed");
    };
    $[9] = afterQueryTime;
    $[10] = beforeParamTime;
    $[11] = beforeQueryTime;
    $[12] = checkTimeElapsed;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== body || $[16] !== faqList || $[17] !== keyTakeaways?.length) {
    const getOptions = () => {
      const options = [];
      if (keyTakeaways?.length) {
        options.push({
          id: "key-takeaways",
          label: "Key Takeaways",
          value: "key-takeaways"
        });
      }
      if (body) {
        body.forEach(block => {
          if (block._type === "block" && block.style === "h2" && block.children?.[0]?.text) {
            const headingText = sanitizeHeadingId(block.headingId);
            if (headingText) {
              options.push({
                id: headingText,
                label: block.children[0].text,
                value: headingText
              });
            }
          }
        });
      }
      if (faqList) {
        options.push({
          id: "faq",
          label: "FAQs",
          value: "faq"
        });
      }
      return options;
    };
    t8 = getOptions();
    $[15] = body;
    $[16] = faqList;
    $[17] = keyTakeaways?.length;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const options_0 = t8;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <nav className="mt-[24px] text-sm md:mt-[36px]"><ul className="m-0 flex w-full flex-row flex-wrap justify-start gap-1.5 whitespace-normal p-0"><li className="min-w-0 capitalize"><span className="inline-flex min-w-0 items-center gap-1.5"><BaseLink className="break-words text-[#373A36] underline" href="/glossary">Glossary</BaseLink></span></li></ul></nav>;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== title) {
    t10 = <h1 className="article-title overflow-hidden text-ellipsis font-[Lora] text-[36px] font-semibold leading-[46px] text-white">{title}</h1>;
    $[20] = title;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== definition) {
    t11 = definition && <p id="definition" className="speakable__summary font-['Work_Sans'] text-base font-semibold leading-[26px] text-white">{definition}</p>;
    $[22] = definition;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <div className="my-6 flex flex-col items-start gap-4 md:my-9"><div className="flex flex-col items-start gap-6 self-stretch rounded-md bg-[#002330] p-6">{t10}{t11}</div></div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== data) {
    t13 = <ResourceInfo data={data} />;
    $[27] = data;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t13) {
    t14 = <GridRow><GridColumn>{t9}{t12}{t13}</GridColumn></GridRow>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex w-full items-center justify-center" />;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== keyTakeaways) {
    t16 = keyTakeaways?.length && <><h2 id="key-takeaways" className="text-[24px] text-[#373A36] [scroll-margin-top:calc(var(--site-header-height)+8px)]">Key Takeaways</h2><ResourceKeyList data={keyTakeaways} /></>;
    $[33] = keyTakeaways;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== data) {
    t17 = <ResourceContent data={data} />;
    $[35] = data;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== referenceModule) {
    t18 = referenceModule && <ResourceReferences data={referenceModule} />;
    $[37] = referenceModule;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== data) {
    t19 = data?.author && <AuthorInfo data={data} />;
    $[39] = data;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== faqList) {
    t20 = faqList && <FAQ data={faqList} />;
    $[41] = faqList;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ResourceFooter />;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t16 || $[45] !== t17 || $[46] !== t18 || $[47] !== t19 || $[48] !== t20) {
    t22 = <GridColumn lg={8}><div className="mb-8 flex flex-col items-start gap-[25px]">{t15}{t16}{t17}{t18}{t19}{t20}{t21}</div></GridColumn>;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <h2 className="px-[24px] py-0 text-[1.5rem]">Table of Contents</h2>;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== options_0) {
    t24 = <GridColumn lg={4} className={cssUtilities.desktop}><div className="sticky top-[140px] mt-[25px] flex flex-col items-start gap-4 rounded-[6px] bg-white">{t23}<ResourceTableOfContents options={options_0} /></div></GridColumn>;
    $[51] = options_0;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t22 || $[54] !== t24) {
    t25 = <GridRow>{t22}{t24}</GridRow>;
    $[53] = t22;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== options_0 || $[57] !== t25) {
    t26 = <ResourceNavProvider navOptions={options_0}>{t25}</ResourceNavProvider>;
    $[56] = options_0;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== latestArticles) {
    t27 = latestArticles && <ProductArticles id="related-articles" data={latestArticles} />;
    $[59] = latestArticles;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <GlossaryTermClientContent />;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t14 || $[63] !== t26 || $[64] !== t27) {
    t29 = <div className="mx-auto">{t14}{t26}{t27}{t28}</div>;
    $[62] = t14;
    $[63] = t26;
    $[64] = t27;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  return t29;
};
export default GlossaryTerm;
function _temp(module) {
  return module._type === "faqList";
}
function _temp2(module_0) {
  return module_0._type === "referenceModule";
}